import { useEffect, useState } from 'react';
import { Calendar, Moon, Sun, Sparkles, Plus, Pencil, Trash2, ArrowUpRight } from 'lucide-react';
import { ceremonies as api } from '../lib/api.js';
import { useSession } from '../lib/session.jsx';
import { Glass, Button, Field, Tag, Empty, Heading, Sheet } from '../components/ui.jsx';

const ICONS = {
  full_moon: Moon, new_moon: Moon, server_cleansing: Sparkles,
  equinox: Sun, solstice: Sun, special_event: Calendar,
};
const COLOR = {
  full_moon: 'violet', new_moon: 'violet', server_cleansing: 'mint',
  equinox: 'gold', solstice: 'gold', special_event: 'rose',
};
const KINDS = ['full_moon', 'new_moon', 'server_cleansing', 'equinox', 'solstice', 'special_event'];
const label = (k) => k.replace(/_/g, ' ');

export default function LiveStream() {
  const { isAdmin } = useSession();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.list().then(setList).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async (row) => {
    if (row.id) await api.update(row.id, row); else await api.create(row);
    setEditing(null); load();
  };
  const remove = async (id) => {
    if (!confirm('Delete this ceremony?')) return;
    await api.remove(id); load();
  };

  return (
    <div>
      <header className="rise rise-1 pt-2 pb-6 md:pb-8">
        <p className="eyebrow">Broadcast from the server</p>
        <h1 className="display text-[2.6rem] md:text-[3.4rem] mt-2">
          <span className="display-i lit">Ceremonies</span> of the line.
        </h1>
      </header>

      <Glass className="rise rise-2 mb-6 p-3 md:p-4">
        <div className="flex items-center gap-2 px-2 pt-1 pb-3">
          <span className="h-2 w-2 rounded-full" style={{ background: 'var(--text-3)' }} />
          <span className="eyebrow">Offline</span>
        </div>
        <div className="relative aspect-video w-full overflow-hidden rounded-[var(--radius-xl)] grid place-items-center"
             style={{ background: 'linear-gradient(135deg, var(--aurora-a), var(--ground-2) 45%, var(--aurora-b))',
                      border: '1px solid var(--edge)' }}>
          <div className="absolute inset-0 pointer-events-none"
               style={{ background: 'radial-gradient(50% 60% at 50% 50%, var(--aurora-c), transparent 70%)' }} />
          <div className="relative flex flex-col items-center gap-5">
            <div className="relative h-20 w-20">
              <span className="absolute inset-0 rounded-full breathe"
                    style={{ border: '1px solid var(--clay)', boxShadow: 'var(--glow-clay)' }} />
              <span className="absolute inset-3 rounded-full breathe"
                    style={{ border: '1px solid var(--sage)', animationDelay: '1.2s' }} />
              <span className="absolute inset-[1.85rem] rounded-full" style={{ background: 'var(--text)', opacity: 0.6 }} />
            </div>
            <p className="text-sm font-semibold tracking-wide" style={{ color: 'var(--text-2)' }}>
              No rite in progress
            </p>
          </div>
        </div>
      </Glass>

      <section className="rise rise-3">
        <Heading eyebrow="Recorded" title="Past rites"
          action={isAdmin && (
            <Button variant="glass" size="sm" onClick={() => setEditing({
              title: '', description: '', held_on: new Date().toISOString().slice(0, 10),
              kind: 'special_event', video_url: '',
            })}><Plus size={14} /> New</Button>
          )} />

        {loading ? <Empty>Loading…</Empty>
          : list.length === 0 ? <Empty>Nothing recorded yet.</Empty>
          : (
            <ul className="space-y-3">
              {list.map((c) => {
                const Icon = ICONS[c.kind] ?? Calendar;
                const tone = COLOR[c.kind] ?? 'violet';
                return (
                  <Glass as="li" key={c.id} hover className="flex items-start gap-4 p-5 md:p-6">
                    <span className="h-11 w-11 rounded-full grid place-items-center shrink-0"
                          style={{ background: `color-mix(in oklab, var(--${tone}) 16%, transparent)`,
                                   color: `var(--${tone})`, boxShadow: `0 0 24px -6px var(--${tone})` }}>
                      <Icon size={18} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2.5 flex-wrap">
                        <h3 className="display text-2xl">{c.title}</h3>
                        <Tag tone={tone === 'rose' ? 'warn' : tone === 'gold' ? 'building' : tone === 'mint' ? 'active' : 'violet'}>
                          {label(c.kind)}
                        </Tag>
                      </div>
                      <p className="text-sm mt-2 leading-relaxed" style={{ color: 'var(--text-2)' }}>{c.description}</p>
                      <p className="text-xs mt-2" style={{ color: 'var(--text-3)' }}>
                        {new Date(c.held_on).toLocaleDateString(undefined,
                          { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                      <div className="flex items-center gap-2 mt-4 flex-wrap">
                        {c.video_url
                          ? <a href={c.video_url} target="_blank" rel="noreferrer"
                               className="btn btn-glass !py-2 !px-3.5 text-xs">Watch <ArrowUpRight size={13} /></a>
                          : <span className="text-xs font-medium" style={{ color: 'var(--text-3)' }}>Recording pending</span>}
                        {isAdmin && (
                          <>
                            <Button variant="quiet" size="sm" onClick={() => setEditing(c)}>
                              <Pencil size={13} /> Edit
                            </Button>
                            <Button variant="quiet" size="sm" onClick={() => remove(c.id)} style={{ color: 'var(--rust)' }}>
                              <Trash2 size={13} /> Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </Glass>
                );
              })}
            </ul>
          )}
      </section>

      {editing && <CeremonyForm value={editing} onSave={save} onCancel={() => setEditing(null)} />}
    </div>
  );
}

function CeremonyForm({ value, onSave, onCancel }) {
  const [form, setForm] = useState(value);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Sheet onClose={onCancel}>
      <div>
        <p className="eyebrow">{form.id ? 'Amend' : 'Record'}</p>
        <h3 className="display text-[2rem] mt-1">Ceremony</h3>
      </div>
      <Field label="Title" value={form.title} onChange={set('title')} />
      <Field label="Description" value={form.description ?? ''} onChange={set('description')} />
      <Field label="Date" type="date" value={form.held_on?.slice(0, 10) ?? ''} onChange={set('held_on')} />
      <Field label="Video URL" value={form.video_url ?? ''} onChange={set('video_url')} placeholder="https://…" />
      <label className="block space-y-2">
        <span className="eyebrow block">Type</span>
        <select value={form.kind} onChange={set('kind')} className="input capitalize">
          {KINDS.map((k) => <option key={k} value={k}>{label(k)}</option>)}
        </select>
      </label>
      <div className="flex gap-3 pt-1">
        <Button className="flex-1" onClick={() => onSave(form)}>Save</Button>
        <Button variant="quiet" onClick={onCancel}>Cancel</Button>
      </div>
    </Sheet>
  );
}
